import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, CartProvider, ThemeProvider, useAuth } from './lib/store';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import CartPage from './pages/CartPage';
import QuotationsPage from './pages/QuotationsPage';
import QuotationDetailPage from './pages/QuotationDetailPage';
import CustomerProfilePage from './pages/CustomerProfilePage';
import AdminPage from './pages/AdminPage';
import AIImportPage from './pages/AIImportPage';
import StockManagementPage from './pages/StockManagementPage';
import DeliveryPage from './pages/DeliveryPage';
import PurchaseOrderPage from './pages/PurchaseOrderPage';
import StockHistoryPage from './pages/StockHistoryPage';
import QrCodesPage from './pages/QrCodesPage';
import QrViewPage from './pages/QrViewPage';
import MyAnalyticsPage from './pages/MyAnalyticsPage';
import MyRegistrationsPage from './pages/MyRegistrationsPage';
import MyPendingQuotesPage from './pages/MyPendingQuotesPage';
import CatalogHealthPage from './pages/CatalogHealthPage';
import MyCollectionsPage from './pages/MyCollectionsPage';
import CollectionsHubPage from './pages/CollectionsHubPage';
import AdminCollectionsUserDetailPage from './pages/AdminCollectionsUserDetailPage';
import ComparisonAnalyticsPage from './pages/ComparisonAnalyticsPage';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-[#8F9485]">Loading...</div>;
  if (!user) {
    // Preserve the original path so users land back here after logging in
    // (matters for /qr/:id deep links from scanned stickers).
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-[#8F9485]">Loading...</div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={new URLSearchParams(window.location.search).get('redirect') || '/'} replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Layout><ProductsPage /></Layout></ProtectedRoute>} />
      <Route path="/cart" element={<ProtectedRoute><Layout><CartPage /></Layout></ProtectedRoute>} />
      <Route path="/quotations" element={<ProtectedRoute><Layout><QuotationsPage /></Layout></ProtectedRoute>} />
      <Route path="/quotations/:id" element={<ProtectedRoute><Layout><QuotationDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><Layout><CustomerProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Layout><AdminPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/comparison-analytics" element={<ProtectedRoute><Layout><ComparisonAnalyticsPage /></Layout></ProtectedRoute>} />
      <Route path="/stock" element={<ProtectedRoute><Layout><StockManagementPage /></Layout></ProtectedRoute>} />
      <Route path="/stock/history" element={<ProtectedRoute><Layout><StockHistoryPage /></Layout></ProtectedRoute>} />
      <Route path="/delivery" element={<ProtectedRoute><Layout><DeliveryPage /></Layout></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute><Layout><PurchaseOrderPage /></Layout></ProtectedRoute>} />
      <Route path="/ai-import" element={<ProtectedRoute><AIImportPage /></ProtectedRoute>} />
      <Route path="/qr-codes" element={<ProtectedRoute><Layout><QrCodesPage /></Layout></ProtectedRoute>} />
      <Route path="/qr/:id" element={<ProtectedRoute><QrViewPage /></ProtectedRoute>} />
      <Route path="/my-analytics" element={<ProtectedRoute><Layout><MyAnalyticsPage /></Layout></ProtectedRoute>} />
      <Route path="/my-registrations" element={<ProtectedRoute><Layout><MyRegistrationsPage /></Layout></ProtectedRoute>} />
      <Route path="/my-pending-quotes" element={<ProtectedRoute><Layout><MyPendingQuotesPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/catalog-health" element={<ProtectedRoute><Layout><CatalogHealthPage /></Layout></ProtectedRoute>} />
      <Route path="/my-collections" element={<ProtectedRoute><Layout><MyCollectionsPage /></Layout></ProtectedRoute>} />
      <Route path="/collections-hub" element={<ProtectedRoute><Layout><CollectionsHubPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/collections/:userId" element={<ProtectedRoute><Layout><AdminCollectionsUserDetailPage /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <Toaster position="bottom-right" richColors />
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
